import React, { useState, useEffect } from 'react';
import { Package, ShoppingCart, Users, MessageSquare, TrendingUp, PoundSterling } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  const statCards = stats ? [
    { label: 'Total Products', value: stats.total_products, icon: Package, color: 'text-blue-600' },
    { label: 'Total Orders', value: stats.total_orders, icon: ShoppingCart, color: 'text-green-600' },
    { label: 'Pending Orders', value: stats.pending_orders, icon: TrendingUp, color: 'text-yellow-600' },
    { label: 'Confirmed Orders', value: stats.confirmed_orders, icon: ShoppingCart, color: 'text-emerald-600' },
    { label: 'Subscribers', value: stats.total_subscribers, icon: Users, color: 'text-purple-600' },
    { label: 'Unread Messages', value: stats.unread_messages, icon: MessageSquare, color: 'text-red-600' },
  ] : [];

  return (
    <div data-testid="admin-dashboard" className="p-6 lg:p-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold tracking-tight">
          Dashboard
        </h1>
        <p className="mt-2 text-neutral-500">Welcome back! Here's an overview of your store.</p>
      </motion.div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {statCards.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-neutral-200 p-6"
              >
                <div className="flex items-center justify-between">
                  <span className="text-neutral-500 text-sm uppercase tracking-wider">{stat.label}</span>
                  <stat.icon size={24} className={stat.color} />
                </div>
                <p className="text-4xl font-bold mt-4">{stat.value}</p>
              </motion.div>
            ))}
          </div>

          {/* Revenue Card */}
          {stats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-6 bg-[#e84628] text-white p-8"
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-neutral-400 text-sm uppercase tracking-wider">Total Revenue</span>
                  <p className="text-5xl font-bold mt-2">
                    £{stats.total_revenue.toFixed(2)}
                  </p>
                </div>
                <div className="w-16 h-16 bg-white/10 flex items-center justify-center">
                  <PoundSterling size={32} />
                </div>
              </div>
            </motion.div>
          )}

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-10"
          >
            <h2 className="font-semibold uppercase tracking-wider text-sm mb-6">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="/admin/products"
                className="p-6 bg-white border border-neutral-200 hover:border-black transition-colors"
              >
                <Package size={24} className="mb-4" />
                <h3 className="font-semibold">Manage Products</h3>
                <p className="text-neutral-500 text-sm mt-1">Add, edit, or remove products</p>
              </a>
              <a
                href="/admin/orders"
                className="p-6 bg-white border border-neutral-200 hover:border-black transition-colors"
              >
                <ShoppingCart size={24} className="mb-4" />
                <h3 className="font-semibold">View Orders</h3>
                <p className="text-neutral-500 text-sm mt-1">Track and manage orders</p>
              </a>
              <a
                href="/admin/messages"
                className="p-6 bg-white border border-neutral-200 hover:border-black transition-colors"
              >
                <MessageSquare size={24} className="mb-4" />
                <h3 className="font-semibold">Check Messages</h3>
                <p className="text-neutral-500 text-sm mt-1">Respond to customer inquiries</p>
              </a>
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
